import { inject, Injectable } from '@angular/core';
import {
  type CardTaskDetailFragment,
  type CreateCardTaskInput,
  type CreateCardTaskMutation,
  type CreateCardTaskMutationVariables,
  type EventCardTasksQueryQuery,
  type EventCardTasksQueryQueryVariables,
  type GoalCardTasksQueryQuery,
  type GoalCardTasksQueryQueryVariables,
  type UpdateCardTaskInput,
  type UpdateCardTaskMutation,
  type UpdateCardTaskMutationVariables,
} from '@reconcept-platform/shared/types';
import { gql } from 'apollo-angular';
import { type Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { type CardTaskStatusEnum } from '../../schema/schema-enums';
import { getLogRefetchQueries } from '../../utils/log-query-refetch';
import { WorkspaceGraphQLService } from '../../workspace-graphql.service';
import { ViewerWithCardsAndTasksQuery } from '../viewer/viewer.queries';
import { cardTaskDetailFragment } from './task.fragments';
import { EventCardTasksQuery, GoalCardTasksQuery } from './task.queries';

@Injectable({
  providedIn: 'root',
})
export class TaskApi {
  private workspaceGraphQLService = inject(WorkspaceGraphQLService);

  watchGoalCardTasksWithUsers(
    goalCardIds: string[],
    status: CardTaskStatusEnum[],
  ): Observable<CardTaskDetailFragment[]> {
    return (
      this.workspaceGraphQLService
        .watchQuery<GoalCardTasksQueryQuery, GoalCardTasksQueryQueryVariables>({
          query: GoalCardTasksQuery,
          variables: {
            goalCardIds,
            status,
          },
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.cardTasks.nodes))
    );
  }

  watchEventCardTasksWithUsers(
    eventCardIds: string[],
    status: CardTaskStatusEnum[],
  ): Observable<CardTaskDetailFragment[]> {
    return (
      this.workspaceGraphQLService
        .watchQuery<EventCardTasksQueryQuery, EventCardTasksQueryQueryVariables>({
          query: EventCardTasksQuery,
          variables: {
            eventCardIds,
            status,
          },
        })
        // @ts-expect-error TS18049 possibly null or undefined
        .pipe(map((result) => result.data.cardTasks.nodes))
    );
  }

  createCardTask(createCardTaskInput: CreateCardTaskInput) {
    const logRefetchQueries = getLogRefetchQueries(createCardTaskInput.cardId);

    return (
      this.workspaceGraphQLService
        .mutateV2<CreateCardTaskMutation, CreateCardTaskMutationVariables>({
          mutation: gql`
            mutation CreateCardTask($input: CreateCardTaskInput!) {
              createCardTask(input: $input) {
                cardTask {
                  ...CardTaskDetail
                }

                errors {
                  message
                  path
                }
              }
            }
            ${cardTaskDetailFragment}
          `,
          variables: {
            input: createCardTaskInput,
          },
          refetchQueries: [
            ...logRefetchQueries,
            {
              query: ViewerWithCardsAndTasksQuery,
            },
          ],
          update: (cache) => {
            cache.modify({
              fields: {
                cardTasks(existingCardTasks, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.createCardTask.cardTask))
    );
  }

  updateCardTask(cardId: string, updateCardTaskInput: UpdateCardTaskInput) {
    const logRefetchQueries = getLogRefetchQueries(cardId);

    return (
      this.workspaceGraphQLService
        .mutateV2<UpdateCardTaskMutation, UpdateCardTaskMutationVariables>({
          mutation: gql`
            mutation UpdateCardTask($input: UpdateCardTaskInput!) {
              updateCardTask(input: $input) {
                cardTask {
                  ...CardTaskDetail
                }

                errors {
                  message
                  path
                }
              }
            }
            ${cardTaskDetailFragment}
          `,
          variables: {
            input: updateCardTaskInput,
          },
          refetchQueries: [
            ...logRefetchQueries,
            {
              query: ViewerWithCardsAndTasksQuery,
            },
          ],
          update: (cache) => {
            cache.modify({
              fields: {
                viewer(existingCardTasks, { DELETE }) {
                  return DELETE;
                },
              },
            });
          },
        })
        // @ts-expect-error TS18048 possibly undefined | TS18049 possibly null or undefined
        .pipe(map((result) => result.data.updateCardTask.cardTask))
    );
  }
}
